'use client';

import React, { useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DocumentUploadSectionProps {
  uploadedDocs: Record<string, boolean>;
  onDocUpload: (docId: string, uploaded: boolean) => void;
  onProceed: () => void;
  allDocsUploaded: boolean;
}

interface DocItem {
  id: string;
  label: string;
  description: string;
  required: boolean;
  icon: string;
  acceptTypes: string;
  downloadLink?: string;
}

const documents: DocItem[] = [
  {
    id: 'application-form',
    label: 'FCEP Application Form',
    description: 'Download the official FCEP application form, fill it in completely, sign it, and upload a scan or clear photo.',
    required: true,
    icon: 'DocumentArrowDownIcon',
    acceptTypes: '.pdf,.jpg,.jpeg,.png',
    downloadLink: '#',
  },
  {
    id: 'aadhaar-front',
    label: 'Aadhaar Card — Front Side',
    description: 'Upload a clear scan or photo of the front side of your Aadhaar card showing your name, photo, and Aadhaar number.',
    required: true,
    icon: 'IdentificationIcon',
    acceptTypes: '.jpg,.jpeg,.png,.pdf',
  },
  {
    id: 'aadhaar-back',
    label: 'Aadhaar Card — Back Side',
    description: 'Upload the back side of your Aadhaar card showing your address details.',
    required: true,
    icon: 'IdentificationIcon',
    acceptTypes: '.jpg,.jpeg,.png,.pdf',
  },
  {
    id: 'pan-card',
    label: 'PAN Card',
    description: 'Upload a clear photo or scan of your PAN card. Ensure all text is clearly readable.',
    required: true,
    icon: 'CreditCardIcon',
    acceptTypes: '.jpg,.jpeg,.png,.pdf',
  },
  {
    id: 'selfie',
    label: 'Recent Selfie / Photograph',
    description: 'Upload a recent clear selfie or passport-size photo with your face clearly visible against a light background.',
    required: true,
    icon: 'UserCircleIcon',
    acceptTypes: '.jpg,.jpeg,.png',
  },
  {
    id: 'social-proof',
    label: 'Social Media Profile Screenshot',
    description: 'Upload a screenshot of your social media profile page (Facebook, Instagram, YouTube, or TikTok) showing your username and follower count.',
    required: true,
    icon: 'DevicePhoneMobileIcon',
    acceptTypes: '.jpg,.jpeg,.png',
  },
];

export default function DocumentUploadSection({
  uploadedDocs,
  onDocUpload,
  onProceed,
  allDocsUploaded,
}: DocumentUploadSectionProps) {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileChange = (docId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // BACKEND INTEGRATION POINT: Upload file to /api/verification/upload
    onDocUpload(docId, !!file);
  };

  const handleRemove = (docId: string) => {
    if (fileInputRefs.current[docId]) {
      fileInputRefs.current[docId]!.value = '';
    }
    onDocUpload(docId, false);
  };

  const uploadedCount = Object.values(uploadedDocs).filter(Boolean).length;
  const totalRequired = documents.filter((d) => d.required).length;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-700 text-foreground">Upload Required Documents</h2>
          <p className="text-muted-foreground text-sm mt-1">
            All documents must be clear and legible. Accepted formats: JPG, PNG, PDF.
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-700 text-primary">{uploadedCount}/{totalRequired}</p>
          <p className="text-xs text-muted-foreground">uploaded</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-muted rounded-full h-2 mb-6">
        <div
          className="bg-secondary h-2 rounded-full transition-all duration-500"
          style={{ width: `${(uploadedCount / totalRequired) * 100}%` }}
        />
      </div>

      <div className="space-y-4">
        {documents.map((doc) => {
          const isUploaded = uploadedDocs[doc.id];

          return (
            <div
              key={`doc-upload-${doc.id}`}
              className={`border rounded-xl p-4 lg:p-5 transition-all duration-200 ${
                isUploaded
                  ? 'border-secondary bg-green-50' :'border-border bg-card hover:border-primary/40'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isUploaded ? 'bg-secondary' : 'bg-blue-50'
                  }`}
                >
                  {isUploaded ? (
                    <Icon name="CheckIcon" size={20} variant="outline" className="text-white" />
                  ) : (
                    <Icon
                      name={doc.icon as Parameters<typeof Icon>[0]['name']}
                      size={20}
                      variant="outline"
                      className="text-primary"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`text-sm font-700 ${isUploaded ? 'text-secondary' : 'text-foreground'}`}>
                      {doc.label}
                    </h3>
                    {doc.required && (
                      <span className="text-xs text-danger font-500">Required</span>
                    )}
                    {isUploaded && (
                      <span className="status-verified text-xs font-600 px-2 py-0.5 rounded-full">
                        ✓ Uploaded
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {doc.description}
                  </p>

                  {/* Download link for application form */}
                  {doc.downloadLink && !isUploaded && (
                    <a
                      href={doc.downloadLink}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-500 mt-2"
                    >
                      <Icon name="ArrowDownTrayIcon" size={13} variant="outline" />
                      Download FCEP Application Form
                    </a>
                  )}
                </div>

                {/* Upload / Remove action */}
                <div className="flex-shrink-0">
                  {isUploaded ? (
                    <button
                      type="button"
                      onClick={() => handleRemove(doc.id)} className="text-xs text-danger hover:underline font-500 flex items-center gap-1">
                      <Icon name="TrashIcon" size={13} variant="outline" />
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRefs.current[doc.id]?.click()}
                      className="btn-secondary text-xs flex-shrink-0"
                      style={{ padding: '0.375rem 0.875rem' }}
                    >
                      <Icon name="ArrowUpTrayIcon" size={13} variant="outline" />
                      Upload
                    </button>
                  )}
                  <input
                    type="file"
                    accept={doc.acceptTypes}
                    className="hidden"
                    ref={(el) => { fileInputRefs.current[doc.id] = el; }}
                    onChange={(e) => handleFileChange(doc.id, e)}
                    aria-label={`Upload ${doc.label}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Proceed Button */}
      <div className="mt-8 pt-6 border-t border-border">
        {!allDocsUploaded && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <Icon name="ExclamationTriangleIcon" size={16} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
            <p className="text-amber-700 text-sm">
              Please upload all {totalRequired} required documents before proceeding to payment.
              ({uploadedCount} of {totalRequired} uploaded)
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={onProceed}
          disabled={!allDocsUploaded}
          className="btn-primary w-full justify-center text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          style={{ padding: '0.875rem 1.5rem' }}
        >
          <Icon name="ArrowRightIcon" size={20} variant="outline" />
          Proceed to Payment — ₹500 Verification Fee
        </button>
      </div>
    </div>
  );
}